import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import RoiCalculator from "@/components/home/RoiCalculator";
import {
  Calculator,
  Presentation,
  Lightbulb,
  Save,
  Search,
  X,
  Loader2,
  CheckCircle2,
  Building2,
  ChevronDown,
} from "lucide-react";

const USD = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.round(n || 0));

const NUM = (n) => new Intl.NumberFormat("en-US").format(Math.round(n || 0));

export default function AdminRoiCalculator() {
  const [state, setState] = useState(null); // { inputs, outputs } from RoiCalculator
  const [prospects, setProspects] = useState([]);
  const [loadingProspects, setLoadingProspects] = useState(true);
  const [saveOpen, setSaveOpen] = useState(false);
  const [recentSaves, setRecentSaves] = useState([]); // latest 5 for context

  // Capture state from the embedded calculator. useCallback so the child
  // doesn't fire its effect on every parent re-render.
  const onStateChange = useCallback((s) => setState(s), []);

  const loadProspects = useCallback(async () => {
    setLoadingProspects(true);
    try {
      const r = await api.get("/opportunity/prospects");
      setProspects(r.data?.prospects || r.data || []);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not load prospects."
      );
    } finally {
      setLoadingProspects(false);
    }
  }, []);

  useEffect(() => {
    loadProspects();
  }, [loadProspects]);

  return (
    <div
      data-testid="admin-roi-calculator-page"
      className="max-w-6xl mx-auto py-8 px-4 space-y-6"
    >
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1">
            <Presentation className="w-3 h-3" />
            Demo tool · Internal use only
          </div>
          <h1 className="mt-3 font-display text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
            <span className="inline-flex w-10 h-10 rounded-xl bg-slate-900 text-white items-center justify-center">
              <Calculator className="w-5 h-5" />
            </span>
            ROI Calculator
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl leading-relaxed">
            Use this during a prospect demo to model the clinic's cash-pay
            revenue opportunity in real time. Save the scenario to a prospect
            so the exact numbers travel with your follow-up.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setSaveOpen(true)}
          disabled={!state}
          data-testid="admin-roi-save-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 transition-all shadow-sm hover:shadow-lg disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> Save to prospect
        </button>
      </div>

      <div
        data-testid="admin-roi-hint"
        className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 flex items-start gap-3"
      >
        <Lightbulb className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900 leading-relaxed">
          <span className="font-semibold">Tip:</span> Anchor the assumptions to
          the prospect's actual numbers — their patient volume, realistic
          conversion rate, target cash-pay price. The calculator is
          illustrative, not a quote. Save the scenario so your 3-day follow-up
          email cites the exact figures the prospect saw on screen.
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <RoiCalculator embedded onStateChange={onStateChange} />
      </div>

      {recentSaves.length > 0 && (
        <RecentSavesPanel saves={recentSaves} />
      )}

      {saveOpen && state && (
        <SaveModal
          state={state}
          prospects={prospects}
          loadingProspects={loadingProspects}
          onClose={() => setSaveOpen(false)}
          onSaved={(prospect, scenario) => {
            setRecentSaves((prev) =>
              [
                {
                  prospect_id: prospect.id,
                  prospect_name: prospect.clinic_name,
                  ...scenario,
                },
                ...prev,
              ].slice(0, 5)
            );
            setSaveOpen(false);
          }}
          onProspectsChanged={loadProspects}
        />
      )}
    </div>
  );
}

// ---------- Save modal ----------
function SaveModal({
  state,
  prospects,
  loadingProspects,
  onClose,
  onSaved,
  onProspectsChanged,
}) {
  const [mode, setMode] = useState("pick"); // 'pick' | 'create'
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [newClinicName, setNewClinicName] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const filteredProspects = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return prospects.slice(0, 20);
    return prospects
      .filter((p) =>
        [p.clinic_name, p.city, p.state, p.contact_email]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q))
      )
      .slice(0, 20);
  }, [query, prospects]);

  const selectedProspect = useMemo(
    () => prospects.find((p) => p.id === selectedId),
    [prospects, selectedId]
  );

  const handleSave = async () => {
    setSubmitting(true);
    try {
      let prospect = selectedProspect;
      if (mode === "create") {
        if (!newClinicName.trim()) {
          toast.error("Enter a clinic name to create a new prospect.");
          setSubmitting(false);
          return;
        }
        const r = await api.post("/opportunity/prospects", {
          clinic_name: newClinicName.trim(),
          contact_email: newContactEmail.trim() || null,
          source: "roi-calculator-demo",
        });
        prospect = r.data;
        onProspectsChanged?.();
      }
      if (!prospect) {
        toast.error("Pick or create a prospect first.");
        setSubmitting(false);
        return;
      }

      const payload = {
        inputs: {
          conversations: state.inputs.conversations,
          conversion_pct: state.inputs.conversionPct,
          price: state.inputs.price,
          vendor_cost: state.inputs.vendorCost,
          platform_cost: state.inputs.platformCost,
        },
        outputs: {
          monthly_purchases: state.outputs.purchases,
          gross_revenue: state.outputs.grossRevenue,
          vendor_costs: state.outputs.vendorCosts,
          net_monthly: state.outputs.netBefore,
          net_annualized: state.outputs.annualized,
        },
        label: label.trim() || null,
        notes: notes.trim() || null,
      };
      const r = await api.post(
        `/opportunity/prospects/${prospect.id}/roi-scenarios`,
        payload
      );
      toast.success(`Scenario saved to ${prospect.clinic_name}.`);
      onSaved(prospect, r.data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e?.message || "Save failed."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-roi-save-modal"
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-blue-700">
              Save scenario
            </div>
            <h2 className="font-display text-xl font-bold text-slate-900 mt-0.5">
              Attach these numbers to a prospect
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-roi-save-modal-close"
            className="text-slate-400 hover:text-slate-900 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Scenario preview */}
          <div className="rounded-xl bg-slate-900 text-white p-4 grid grid-cols-2 gap-3">
            <PreviewRow
              label="Estimated net / mo"
              value={USD(state.outputs.netBefore)}
              accent
            />
            <PreviewRow
              label="Estimated net / yr"
              value={USD(state.outputs.annualized)}
            />
            <PreviewRow
              label="Conversations"
              value={NUM(state.inputs.conversations)}
              muted
            />
            <PreviewRow
              label="Conversion"
              value={`${state.inputs.conversionPct}%`}
              muted
            />
            <PreviewRow
              label="Avg price"
              value={USD(state.inputs.price)}
              muted
            />
            <PreviewRow
              label="Monthly purchases"
              value={NUM(state.outputs.purchases)}
              muted
            />
          </div>

          {/* Mode toggle */}
          <div className="flex items-center gap-2 text-sm font-semibold border-b border-slate-100">
            <ModeTab
              active={mode === "pick"}
              onClick={() => setMode("pick")}
              testId="admin-roi-save-tab-pick"
            >
              <Search className="w-3.5 h-3.5" /> Existing prospect
            </ModeTab>
            <ModeTab
              active={mode === "create"}
              onClick={() => setMode("create")}
              testId="admin-roi-save-tab-create"
            >
              <Building2 className="w-3.5 h-3.5" /> New prospect
            </ModeTab>
          </div>

          {mode === "pick" ? (
            <div className="space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder={
                    loadingProspects
                      ? "Loading prospects…"
                      : `Search ${prospects.length} prospects by name, city, or email…`
                  }
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  disabled={loadingProspects}
                  data-testid="admin-roi-save-search"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                />
              </div>
              <div className="max-h-56 overflow-y-auto rounded-xl border border-slate-100 divide-y divide-slate-100">
                {loadingProspects ? (
                  <div className="p-6 text-center text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin inline-block" />
                  </div>
                ) : filteredProspects.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500">
                    No prospects match. Try "New prospect" instead.
                  </div>
                ) : (
                  filteredProspects.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedId(p.id)}
                      data-testid={`admin-roi-save-pick-${p.id}`}
                      className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-slate-50 ${
                        selectedId === p.id
                          ? "bg-blue-50 hover:bg-blue-50"
                          : ""
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 text-sm truncate">
                          {p.clinic_name}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {[p.city, p.state, p.contact_email]
                            .filter(Boolean)
                            .join(" · ") || p.clinic_type || "—"}
                        </div>
                      </div>
                      {selectedId === p.id && (
                        <CheckCircle2 className="w-4 h-4 text-blue-700 flex-shrink-0" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <FieldLabel>Clinic name (required)</FieldLabel>
              <input
                type="text"
                value={newClinicName}
                onChange={(e) => setNewClinicName(e.target.value)}
                placeholder="e.g. Apex Functional Medicine"
                data-testid="admin-roi-save-new-clinic"
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
              />
              <FieldLabel>Contact email (optional)</FieldLabel>
              <input
                type="email"
                value={newContactEmail}
                onChange={(e) => setNewContactEmail(e.target.value)}
                placeholder="contact@clinic.com"
                data-testid="admin-roi-save-new-email"
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
              />
              <p className="text-xs text-slate-500">
                A new prospect will be created with{" "}
                <span className="font-mono">source=roi-calculator-demo</span>{" "}
                and this scenario attached.
              </p>
            </div>
          )}

          <div className="space-y-3 pt-2 border-t border-slate-100">
            <FieldLabel>Scenario label (optional)</FieldLabel>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. 'Conservative' or 'Demo on 5/15'"
              data-testid="admin-roi-save-label"
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
            />
            <FieldLabel>Notes (optional)</FieldLabel>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="What did they push back on? Any context worth carrying into the follow-up?"
              data-testid="admin-roi-save-notes"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm leading-relaxed focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-100 bg-slate-50 sticky bottom-0">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={
              submitting ||
              (mode === "pick" && !selectedId) ||
              (mode === "create" && !newClinicName.trim())
            }
            data-testid="admin-roi-save-confirm-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-5 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save scenario
          </button>
        </div>
      </div>
    </div>
  );
}

function ModeTab({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-2 -mb-px border-b-2 transition-colors ${
        active
          ? "border-slate-900 text-slate-900"
          : "border-transparent text-slate-500 hover:text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

function FieldLabel({ children }) {
  return (
    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
      {children}
    </div>
  );
}

function PreviewRow({ label, value, muted, accent }) {
  return (
    <div>
      <div
        className={`text-[10px] uppercase tracking-wider font-semibold ${
          accent ? "text-blue-300" : "text-slate-400"
        }`}
      >
        {label}
      </div>
      <div
        className={`font-display font-bold tabular mt-0.5 ${
          accent
            ? "text-2xl text-white"
            : muted
              ? "text-sm text-slate-300"
              : "text-base text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function RecentSavesPanel({ saves }) {
  return (
    <div
      data-testid="admin-roi-recent-saves"
      className="rounded-2xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
        <ChevronDown className="w-3 h-3" /> Recent saves (this session)
      </div>
      <ul className="space-y-2">
        {saves.map((s) => (
          <li
            key={s.id}
            data-testid={`admin-roi-recent-${s.id}`}
            className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2"
          >
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 text-sm truncate">
                {s.prospect_name}{" "}
                {s.label && (
                  <span className="text-slate-400 font-normal">· {s.label}</span>
                )}
              </div>
              <div className="text-xs text-slate-500">
                {USD(s.outputs.net_monthly)}/mo · {USD(s.outputs.net_annualized)}/yr
              </div>
            </div>
            <a
              href={`/admin/opportunity-engine?prospect=${s.prospect_id}`}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900"
            >
              View prospect →
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
